import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogConfig } from '../dialog/dialog-config';
import { DialogRef } from '../dialog/dialog-ref';
import { BaseService } from '../../shared/services/base.service';
import { AlertsService } from '../../services/alerts.service';
import { LoaderService } from '../../services/loader.service';

// Add Patient by ABHA: a 2-step popup (enter ABHA number -> send OTP, then enter OTP -> verify)
// that fetches read-only demographics from ABDM for a *new*, not-yet-registered patient.
// Closes with the fetched demographics so dhs-form (form.component.ts) can prefill+disable
// the registration form, mirroring the existing QR-code-scan autofill flow.
@Component({
    selector: 'app-abha-patient-register',
    templateUrl: './abha-patient-register.component.html',
    styleUrls: ['./abha-patient-register.component.scss'],
    standalone: false
})
export class AbhaPatientRegisterComponent implements OnInit, OnDestroy {
  step: 'enter_abha' | 'enter_otp' = 'enter_abha';
  abhaNumber: string = '';
  otp: string = '';
  isSubmitting: boolean = false;
  timer: number = 0;
  showTimer: boolean = false;

  private transactionId: string;
  private healthIdTemp: string;
  private healthIdNumberTemp: string;
  private timerInterval: any;

  constructor(
    public dialog: DialogRef,
    private modal: DialogConfig,
    private baseService: BaseService,
    private alerts: AlertsService,
    private loader: LoaderService
  ) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  sendOtp() {
    if (!this.abhaNumber || !this.abhaNumber.trim()) {
      this.alerts.error('Please enter a valid ABHA Number');
      return;
    }
    this.isSubmitting = true;
    this.loader.showSpinner();
    let data = { abdm_number: this.abhaNumber.trim() };
    this.baseService.action(data, 'ABDM_NEW_PATIENT_AUTH', 'patient-registration', 'patient').subscribe((result: any) => {
      this.isSubmitting = false;
      this.loader.hideSpinner();
      if (result.errCode == 0) {
        let resource = this.getResource(result);
        this.transactionId = resource ? resource.transaction_id : undefined;
        this.healthIdTemp = resource ? resource.healthId_temp : undefined;
        this.healthIdNumberTemp = resource ? resource.healthIdNumber_temp : undefined;
        if (!this.transactionId) {
          this.alerts.error('Unable to send OTP for this ABHA Number. Please try again.');
          return;
        }
        this.alerts.info(result.message || 'OTP sent to the mobile number linked with this ABHA Number');
        this.otp = '';
        this.step = 'enter_otp';
        this.startTimer();
      } else {
        this.alerts.error(result.message || 'Please enter a valid ABHA Number');
      }
    }, () => {
      this.isSubmitting = false;
      this.loader.hideSpinner();
      this.alerts.error('Something went wrong while sending OTP. Please try again.');
    });
  }

  resendOtp() {
    if (this.showTimer) {
      return;
    }
    this.sendOtp();
  }

  verifyOtp() {
    if (!this.otp || this.otp.trim().length == 0) {
      this.alerts.error('Please enter the OTP');
      return;
    }
    this.isSubmitting = true;
    this.loader.showSpinner();
    let data: any = {
      abdm_number: this.abhaNumber.trim(),
      aadhaar_otp: this.otp.trim(),
      transaction_id: this.transactionId,
      healthId_temp: this.healthIdTemp,
      healthIdNumber_temp: this.healthIdNumberTemp
    };
    this.baseService.action(data, 'ABDM_NEW_PATIENT_VERIFY_OTP', 'patient-registration', 'patient').subscribe((result: any) => {
      this.isSubmitting = false;
      this.loader.hideSpinner();
      if (result.errCode == 0) {
        let resource = this.getResource(result);
        if (!resource || !resource.abdm_patient_info) {
          this.alerts.error('Unable to fetch patient details from ABDM. Please try again.');
          return;
        }
        this.alerts.success(result.message || 'ABHA Number verified successfully');
        this.dialog.close(this.buildAutofillResponse(resource));
      } else {
        this.alerts.error(result.message || 'Invalid OTP. Please try again.');
      }
    }, () => {
      this.isSubmitting = false;
      this.loader.hideSpinner();
      this.alerts.error('Something went wrong while verifying OTP. Please try again.');
    });
  }

  cancel() {
    this.dialog.close();
  }

  private getResource(result: any) {
    // .action() responses echo back a single (mutated) resource object, but defend against
    // an array-wrapped response too since other query endpoints in this service return arrays.
    return Array.isArray(result.resource) ? result.resource[0] : result.resource;
  }

  private buildAutofillResponse(resource: any) {
    let info = resource.abdm_patient_info || {};
    return {
      name: info.name,
      gender: info.gender,
      dayOfBirth: info.dayOfBirth,
      monthOfBirth: info.monthOfBirth,
      yearOfBirth: info.yearOfBirth,
      mobile: info.telecom || info.mobile,
      address: info,
      abdm_number: resource.abdm_number || this.abhaNumber.trim(),
      healthId: resource.healthId,
      healthIdNumber: resource.healthIdNumber
    };
  }

  private startTimer() {
    this.timer = 60;
    this.showTimer = true;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      this.timer -= 1;
      if (this.timer <= 0) {
        clearInterval(this.timerInterval);
        this.showTimer = false;
      }
    }, 1000);
  }
}
